/**
 * Portfolio Management Hook (HYBRID MODE)
 *
 * Supports both LOCAL and CLOUD modes:
 * - LOCAL: Uses localStorage (default, no auth required)
 * - CLOUD: Uses Supabase (optional, when user enables cloud sync)
 *
 * The hook automatically detects the mode and switches transparently.
 * Components using this hook don't need to change!
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import { StorageService } from '../services/storage';
import { trackTokenAdded } from '../services/analyticsTracker';
import { scheduleDeviceSync } from '../services/deviceSyncService';
import { useHybridStorage } from './useHybridStorage';
import { PortfolioManager } from '../services/portfolioManager';
import type { Database } from '../lib/supabase/types';

export type Portfolio = Database['public']['Tables']['portfolios']['Row'];
export type PortfolioInsert = Database['public']['Tables']['portfolios']['Insert'];
export type PortfolioUpdate = Database['public']['Tables']['portfolios']['Update'];

export type PortfolioToken = Database['public']['Tables']['portfolio_tokens']['Row'];
export type TokenInsert = Database['public']['Tables']['portfolio_tokens']['Insert'];
export type TokenUpdate = Database['public']['Tables']['portfolio_tokens']['Update'];

// Local storage types (backward compatible)
export interface LocalPortfolio {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  isDefault?: boolean;
  isArchived?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LocalToken {
  id: string; // coin_id
  portfolioId: string;
  symbol: string;
  name?: string;
  targetPrice?: number;
  notes?: string;
  amount?: number;
  avgBuyPrice?: number;
}

export interface UsePortfolioReturn {
  // Portfolios
  portfolios: (Portfolio | LocalPortfolio)[];
  activePortfolio: (Portfolio | LocalPortfolio) | null;
  loading: boolean;
  error: Error | null;

  // Mode info
  mode: 'local' | 'cloud';

  // Portfolio actions
  createPortfolio: (data: Partial<PortfolioInsert>) => Promise<Portfolio | LocalPortfolio>;
  updatePortfolio: (id: string, updates: Partial<PortfolioUpdate>) => Promise<Portfolio | LocalPortfolio>;
  deletePortfolio: (id: string) => Promise<void>;
  setActivePortfolio: (id: string) => void;

  // Tokens in active portfolio
  tokens: (PortfolioToken | LocalToken)[];
  addToken: (data: Partial<TokenInsert> & { id?: string; symbol: string }) => Promise<PortfolioToken | LocalToken>;
  updateToken: (id: string, updates: any) => Promise<PortfolioToken | LocalToken>;
  deleteToken: (id: string) => Promise<void>;
}

/**
 * Hook for managing portfolios and tokens (HYBRID MODE)
 *
 * Works in both LOCAL and CLOUD modes transparently.
 *
 * @example
 * ```tsx
 * function PortfolioDashboard() {
 *   const {
 *     portfolios,
 *     activePortfolio,
 *     tokens,
 *     addToken,
 *     mode, // 'local' or 'cloud'
 *   } = usePortfolio();
 *
 *   // Works the same in both modes!
 *   const handleAddBTC = async () => {
 *     await addToken({
 *       id: 'bitcoin',
 *       symbol: 'BTC',
 *       name: 'Bitcoin',
 *     });
 *   };
 *
 *   return <div>Current mode: {mode}</div>;
 * }
 * ```
 */
export function usePortfolio(): UsePortfolioReturn {
  const { mode, userId, isAuthenticated, isInitialized } = useHybridStorage();

  const [portfolios, setPortfolios] = useState<(Portfolio | LocalPortfolio)[]>([]);
  const [activePortfolio, setActivePortfolioState] = useState<(Portfolio | LocalPortfolio) | null>(null);
  const [tokens, setTokens] = useState<(PortfolioToken | LocalToken)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track if we're currently fetching to prevent duplicate calls
  const fetchingRef = useRef(false);
  // Track if initial load is complete - don't show loading spinner on background refetch
  const hasInitiallyLoaded = useRef(false);

  // Fetch portfolios on mount and when mode changes
  // IMPORTANT: Wait for isInitialized to prevent race conditions
  useEffect(() => {
    let initTimeout: NodeJS.Timeout | undefined;
    let isCancelled = false;

    if (!isInitialized) {
      console.log('[usePortfolio] Waiting for auth initialization...');

      // Safety: If initialization takes too long (10s), force load in local mode
      initTimeout = setTimeout(() => {
        if (isCancelled) return;
        console.warn('[usePortfolio] Auth init timeout - forcing local mode fetch');
        setLoading(false);
        hasInitiallyLoaded.current = true;
        // Load from localStorage as fallback
        PortfolioManager.initialize();
        const pmPortfolios = PortfolioManager.getAllPortfolios();
        if (pmPortfolios.length > 0) {
          const localPortfolios = pmPortfolios.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            icon: p.icon,
            color: p.color,
            isDefault: p.id === 'default',
            isArchived: false,
            createdAt: new Date(p.createdAt).toISOString(),
            updatedAt: new Date(p.updatedAt).toISOString(),
          }));
          setPortfolios(localPortfolios);
          if (localPortfolios.length > 0) {
            setActivePortfolioState(localPortfolios[0]);
          }
        }
      }, 10000);

      return () => {
        isCancelled = true;
        if (initTimeout) clearTimeout(initTimeout);
      };
    }

    // Prevent duplicate fetch calls
    if (fetchingRef.current) {
      console.log('[usePortfolio] Already fetching, skipping...');
      return;
    }

    console.log('[usePortfolio] Auth initialized, fetching portfolios...', { mode, userId });
    fetchPortfolios();

    if (mode === 'cloud' && isAuthenticated) {
      // Subscribe to realtime portfolio changes (cloud mode only)
      const portfolioChannel = supabase
        .channel('portfolios_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'portfolios',
          },
          (payload) => {
            console.log('[CLOUD] Portfolio change detected:', payload);

            if (payload.eventType === 'INSERT') {
              setPortfolios((prev) => [...prev, payload.new as Portfolio]);
            } else if (payload.eventType === 'UPDATE') {
              setPortfolios((prev) =>
                prev.map((p) =>
                  p.id === payload.new.id ? (payload.new as Portfolio) : p
                )
              );
              if (activePortfolio && activePortfolio.id === payload.new.id) {
                setActivePortfolioState(payload.new as Portfolio);
              }
            } else if (payload.eventType === 'DELETE') {
              setPortfolios((prev) => prev.filter((p) => p.id !== payload.old.id));
              if (activePortfolio && activePortfolio.id === payload.old.id) {
                setActivePortfolioState(null);
              }
            }
          }
        )
        .subscribe();

      return () => {
        portfolioChannel.unsubscribe();
      };
    }
  }, [mode, isAuthenticated, isInitialized]);

  // Subscribe to token changes for active portfolio
  useEffect(() => {
    if (!activePortfolio) {
      setTokens([]);
      return;
    }

    fetchTokens(activePortfolio.id);

    if (mode === 'cloud' && isAuthenticated) {
      // Subscribe to realtime token changes (cloud mode only)
      const tokenChannel = supabase
        .channel('portfolio_tokens_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'portfolio_tokens',
            filter: `portfolio_id=eq.${activePortfolio.id}`,
          },
          (payload) => {
            console.log('[CLOUD] Token change detected:', payload);

            if (payload.eventType === 'INSERT') {
              setTokens((prev) => [...prev, payload.new as PortfolioToken]);
            } else if (payload.eventType === 'UPDATE') {
              setTokens((prev) =>
                prev.map((t) =>
                  t.id === payload.new.id ? (payload.new as PortfolioToken) : t
                )
              );
            } else if (payload.eventType === 'DELETE') {
              setTokens((prev) => prev.filter((t) => t.id !== payload.old.id));
            }
          }
        )
        .subscribe();

      return () => {
        tokenChannel.unsubscribe();
      };
    }
    // Bug fix (May 2026): include `userId` and `isInitialized` in the deps
    // so the effect re-runs when auth finishes hydrating (e.g. after a
    // browser refresh that goes via the slow path of useHybridStorage).
    // Previously these were missing — for users whose `mode` and
    // `isAuthenticated` happened to stay constant during init, the effect
    // never re-fired and the `loading=true` state stuck forever.
  }, [activePortfolio, mode, isAuthenticated, userId, isInitialized]);

  /**
   * Fetch all portfolios (LOCAL or CLOUD)
   */
  const fetchPortfolios = async () => {
    // Prevent duplicate calls
    if (fetchingRef.current) {
      console.log('[usePortfolio] fetchPortfolios already in progress, skipping');
      return;
    }

    fetchingRef.current = true;
    console.log('[usePortfolio] fetchPortfolios starting...', { mode, userId, isAuthenticated, hasInitiallyLoaded: hasInitiallyLoaded.current });

    try {
      // Only show loading spinner on INITIAL load, not background refetch
      if (!hasInitiallyLoaded.current) {
        setLoading(true);
      }
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: Fetch from Supabase
        // Bug fix (May 2026, v5) : STALE-WHILE-REVALIDATE.
        // Show cached portfolios immediately, then revalidate from cloud.
        // If the cloud query returns empty (race with Supabase client init),
        // KEEP the cache instead of wiping the user's data.
        const cachedPortfolios = StorageService.get<Portfolio[]>('portfolios');
        if (cachedPortfolios && cachedPortfolios.length > 0) {
          console.log('[CLOUD] Showing cached portfolios immediately:', cachedPortfolios.length);
          setPortfolios(cachedPortfolios);
          const defaultCached = cachedPortfolios.find((p) => p.is_default);
          setActivePortfolioState(defaultCached || cachedPortfolios[0]);
        }

        console.log('[CLOUD] Fetching portfolios from Supabase for user:', userId);

        // Add timeout to prevent hanging
        const fetchPromise = supabase
          .from('portfolios')
          .select('*')
          .eq('user_id', userId)
          .eq('is_archived', false)
          .order('created_at', { ascending: true });

        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Portfolio fetch timeout (5s)')), 5000);
        });

        // Helper that re-issues the same query — used by the retry-on-empty
        // path below. Cheap because Supabase client memoizes the build URL
        // and re-uses the open HTTP connection.
        const buildPortfolioQuery = () =>
          supabase
            .from('portfolios')
            .select('*')
            .eq('user_id', userId)
            .eq('is_archived', false)
            .order('created_at', { ascending: true });

        let data, fetchError;
        try {
          const result = await Promise.race([fetchPromise, timeoutPromise]);
          data = result.data;
          fetchError = result.error;

          // Bug fix (May 2026): retry once on empty result.
          // After a browser refresh, useHybridStorage's fast path sets
          // `mode='cloud'` synchronously from the localStorage session, and
          // fetchPortfolios fires immediately. The Supabase client may not
          // have finished syncing its internal session yet — the query
          // succeeds (no error) but returns 0 rows because RLS evaluates
          // `auth.uid()` as null. Wait 1 s for the client to catch up and
          // retry once. This matches the pattern that makes useWallets
          // (which fires later in the mount sequence) work today.
          if ((!data || data.length === 0) && !fetchError && !hasInitiallyLoaded.current) {
            console.log('[CLOUD] Empty portfolios on first try — retrying after 1 s (Supabase client likely warming up)');
            await new Promise(resolve => setTimeout(resolve, 1000));
            const retryTimeout = new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('Portfolio retry timeout (5s)')), 5000);
            });
            try {
              const retryResult = await Promise.race([buildPortfolioQuery(), retryTimeout]);
              if (retryResult.data && retryResult.data.length > 0) {
                console.log('[CLOUD] Retry succeeded with', retryResult.data.length, 'portfolios');
                data = retryResult.data;
                fetchError = null;
              } else {
                console.log('[CLOUD] Retry also returned empty — user has no cloud portfolios');
              }
            } catch {
              console.warn('[CLOUD] Retry timed out, keeping original empty result');
            }
          }
        } catch (timeoutErr: any) {
          console.error('[CLOUD] Portfolio fetch timeout:', timeoutErr.message);

          // On timeout, FIRST try local data from PortfolioManager (has tokens)
          PortfolioManager.initialize();
          const pmPortfolios = PortfolioManager.getAllPortfolios();

          if (pmPortfolios.length > 0 && pmPortfolios.some(p => p.tokens && p.tokens.length > 0)) {
            console.log('[CLOUD] Timeout - using LOCAL portfolios with tokens:', pmPortfolios.length);

            const localPortfolios: LocalPortfolio[] = pmPortfolios.map(p => ({
              id: p.id,
              name: p.name,
              description: p.description,
              icon: p.icon,
              color: p.color,
              isDefault: p.id === 'default',
              isArchived: false,
              createdAt: new Date(p.createdAt).toISOString(),
              updatedAt: new Date(p.updatedAt).toISOString(),
            }));

            setPortfolios(localPortfolios);
            const activeLocalId = PortfolioManager.getActivePortfolioId() || 'default';
            const activePortfolio = localPortfolios.find((p) => p.id === activeLocalId) || localPortfolios[0];
            console.log('[CLOUD] Timeout - setting active LOCAL portfolio:', activePortfolio.id);
            setActivePortfolioState(activePortfolio);
            setLoading(false);
            hasInitiallyLoaded.current = true;
            fetchingRef.current = false;
            return;
          }

          // Fallback to cached cloud portfolios if no local data with tokens
          const cachedPortfolios = StorageService.get<Portfolio[]>('portfolios', []);
          if (cachedPortfolios.length > 0) {
            console.log('[CLOUD] Using cached portfolios:', cachedPortfolios.length);
            setPortfolios(cachedPortfolios);
            const defaultPortfolio = cachedPortfolios.find((p) => p.is_default);
            setActivePortfolioState(defaultPortfolio || cachedPortfolios[0]);
            setLoading(false);
            hasInitiallyLoaded.current = true;
            fetchingRef.current = false;
            return;
          }
          throw timeoutErr;
        }

        if (fetchError) {
          console.error('[CLOUD] Error fetching portfolios:', fetchError);
          throw fetchError;
        }

        console.log('[CLOUD] Portfolios found:', data?.length || 0);

        // FALLBACK: If cloud is empty, check if there's local data to use
        // This handles the case where user has local data but hasn't migrated yet
        if (!data || data.length === 0) {
          console.log('[CLOUD] No cloud portfolios found, checking local data...');
          PortfolioManager.initialize();

          // Get portfolios from PortfolioManager (not just raw localStorage)
          const pmPortfolios = PortfolioManager.getAllPortfolios();

          if (pmPortfolios.length > 0) {
            console.log('[CLOUD] Found local portfolios via PortfolioManager, using as fallback:', pmPortfolios.length);

            // Convert PM portfolios to LocalPortfolio format
            const localPortfolios: LocalPortfolio[] = pmPortfolios.map(p => ({
              id: p.id,
              name: p.name,
              description: p.description,
              icon: p.icon,
              color: p.color,
              isDefault: p.id === 'default',
              isArchived: false,
              createdAt: new Date(p.createdAt).toISOString(),
              updatedAt: new Date(p.updatedAt).toISOString(),
            }));

            setPortfolios(localPortfolios);

            // Use local active portfolio ID
            const activeLocalId = PortfolioManager.getActivePortfolioId() || 'default';
            const activePortfolio = localPortfolios.find((p) => p.id === activeLocalId) || localPortfolios[0];
            console.log('[CLOUD] Setting active local portfolio:', activePortfolio.id, activePortfolio.name);
            setActivePortfolioState(activePortfolio);
            setLoading(false);
            hasInitiallyLoaded.current = true;
            fetchingRef.current = false;
            return; // Skip cloud portfolio creation
          }
        }

        // CRITICAL: Don't wipe the cache or auto-create a duplicate
        // portfolio if the query came back empty but we have a cached
        // portfolio. The empty is most likely the RLS-without-token
        // race; trust the cache.
        if ((!data || data.length === 0) && cachedPortfolios && cachedPortfolios.length > 0) {
          console.warn('[CLOUD] Query returned empty but cache has portfolios — keeping cache, NOT creating a new portfolio');
          return;
        }

        setPortfolios(data || []);

        // Cache in localStorage (only when we have real data)
        if (data && data.length > 0) {
          StorageService.set('portfolios', data);
        }

        // Set active portfolio
        if (data && data.length > 0) {
          const defaultPortfolio = data.find((p) => p.is_default);
          setActivePortfolioState(defaultPortfolio || data[0]);
        } else {
          // No portfolios + no cache — only then create a default.
          await createDefaultPortfolio();
        }
      } else {
        // LOCAL MODE: Fetch from localStorage
        console.log('[LOCAL] Fetching portfolios from localStorage...');

        // CRITICAL FIX: Initialize PortfolioManager to migrate legacy 'portfolio_tokens' data
        // This ensures backward compatibility with data stored before Supabase integration
        PortfolioManager.initialize();

        // Now fetch portfolios (PortfolioManager.initialize() may have created default portfolio)
        const localPortfolios = StorageService.get<LocalPortfolio[]>('portfolios', []);

        setPortfolios(localPortfolios);

        if (localPortfolios.length > 0) {
          const defaultPortfolio = localPortfolios.find((p) => p.isDefault);
          setActivePortfolioState(defaultPortfolio || localPortfolios[0]);
        } else {
          // No portfolios - create default one
          await createDefaultPortfolio();
        }
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching portfolios:', err);
    } finally {
      // CRITICAL: Always set loading to false and reset fetching flag
      console.log('[usePortfolio] fetchPortfolios complete, setting loading=false');
      setLoading(false);
      fetchingRef.current = false;
      hasInitiallyLoaded.current = true;
    }
  };

  /**
   * Create default portfolio (LOCAL or CLOUD)
   */
  const createDefaultPortfolio = async () => {
    try {
      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        const { data, error: createError } = await supabase
          .from('portfolios')
          .insert({
            user_id: userId,
            name: 'Mon Portfolio Principal',
            description: 'Portfolio par défaut',
            is_default: true,
          })
          .select()
          .single();

        if (createError) throw createError;

        setPortfolios([data]);
        setActivePortfolioState(data);

        // Cache in localStorage
        StorageService.set('portfolios', [data]);

        return data;
      } else {
        // LOCAL MODE
        const localPortfolio: LocalPortfolio = {
          id: crypto.randomUUID(),
          name: 'Mon Portfolio Principal',
          description: 'Portfolio par défaut',
          icon: '💼',
          color: '#8b5cf6',
          isDefault: true,
          isArchived: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const newPortfolios = [localPortfolio];
        StorageService.set('portfolios', newPortfolios);

        setPortfolios(newPortfolios);
        setActivePortfolioState(localPortfolio);

        return localPortfolio;
      }
    } catch (err) {
      console.error('Error creating default portfolio:', err);
      throw err;
    }
  };

  /**
   * Fetch tokens for specific portfolio (LOCAL or CLOUD)
   */
  const fetchTokens = async (portfolioId: string) => {
    try {
      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        console.log('[CLOUD] Fetching tokens for portfolio:', portfolioId);

        // Bug fix (May 2026, v5) : STALE-WHILE-REVALIDATE.
        // Per the user's insight ("there are 13 tokens in cache but the
        // dashboard shows 0"), the previous behavior would overwrite a
        // valid cache with an empty result if the Supabase query came
        // back with [] (race with the client session sync). The cache
        // wins as long as the cloud query doesn't bring fresh data.
        //
        // Logic :
        //   1. Read `tokens_${portfolioId}` from localStorage and show it
        //      immediately (no flicker between login and refresh).
        //   2. Fire the cloud query in the background.
        //   3. If the cloud query returns NON-EMPTY → adopt fresh data.
        //   4. If it returns EMPTY → keep the cache (don't wipe it).
        //   5. If it errors / times out → keep the cache.
        const cachedTokens = StorageService.get<PortfolioToken[]>(`tokens_${portfolioId}`);
        if (cachedTokens && cachedTokens.length > 0) {
          console.log('[CLOUD] Showing cached tokens immediately:', cachedTokens.length);
          setTokens(cachedTokens);
        }

        // Same race-on-refresh pattern as fetchPortfolios + fetchPositions :
        // when this fires immediately after the useHybridStorage fast path,
        // the Supabase client may not have synced its session yet, RLS
        // returns 0 rows. We add a 5 s timeout (don't hang forever) and
        // retry once after 1 s on empty result.
        const buildTokensQuery = () =>
          supabase
            .from('portfolio_tokens')
            .select('*')
            .eq('user_id', userId)
            .eq('portfolio_id', portfolioId)
            .eq('is_archived', false)
            .order('created_at', { ascending: true });

        const buildTimeout = () => new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Tokens fetch timeout (5s)')), 5000);
        });

        let data: any, fetchError: any;
        try {
          const result = await Promise.race([buildTokensQuery(), buildTimeout()]);
          data = result.data;
          fetchError = result.error;

          // Retry once on empty (race with Supabase client init)
          if ((!data || data.length === 0) && !fetchError) {
            console.log('[CLOUD] Empty tokens on first try — retrying after 1 s');
            await new Promise(resolve => setTimeout(resolve, 1000));
            try {
              const retryResult = await Promise.race([buildTokensQuery(), buildTimeout()]);
              if (retryResult.data && retryResult.data.length > 0) {
                console.log('[CLOUD] Tokens retry succeeded with', retryResult.data.length, 'rows');
                data = retryResult.data;
                fetchError = null;
              } else {
                console.log('[CLOUD] Tokens retry also empty');
              }
            } catch {
              console.warn('[CLOUD] Tokens retry timed out');
            }
          }
        } catch (timeoutErr: any) {
          console.warn('[CLOUD] Tokens fetch timeout — keeping cache if any');
          // Don't overwrite — keep whatever was set by the cache step.
          return;
        }

        if (fetchError) {
          console.error('[CLOUD] Error fetching tokens — keeping cache:', fetchError);
          // Don't overwrite — keep cache.
          return;
        }

        console.log('[CLOUD] Tokens found:', data?.length || 0);

        // CRITICAL: Don't wipe the cache with empty data.
        // If the query came back empty but we have cached tokens, the
        // empty is most likely an RLS-without-token race; keep cache.
        if ((!data || data.length === 0) && cachedTokens && cachedTokens.length > 0) {
          console.warn('[CLOUD] Query returned empty but cache has tokens — keeping cache');
          return;
        }

        // FALLBACK: If cloud has no tokens AND no cache, try local data
        if (!data || data.length === 0) {
          console.log('[CLOUD] No cloud tokens found, checking PortfolioManager local data...');

          // Try with the cloud portfolioId first, then fallback to local active portfolio
          let pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
          if (!pmPortfolio || !pmPortfolio.tokens || pmPortfolio.tokens.length === 0) {
            // Cloud ID didn't work, try local active portfolio or 'default'
            const localActiveId = PortfolioManager.getActivePortfolioId() || 'default';
            console.log('[CLOUD] Trying local portfolio ID:', localActiveId);
            pmPortfolio = PortfolioManager.getPortfolio(localActiveId);
          }

          if (pmPortfolio && pmPortfolio.tokens && pmPortfolio.tokens.length > 0) {
            console.log('[CLOUD] Found local tokens, using as fallback:', pmPortfolio.tokens.length);
            const localTokens: LocalToken[] = pmPortfolio.tokens.map(t => ({
              id: t.id,
              portfolioId: portfolioId,
              symbol: t.displayName,
              name: t.displayName,
              targetPrice: t.targetPrice,
              notes: t.note,
              amount: 0,
              avgBuyPrice: 0,
            }));
            setTokens(localTokens);
            return;
          }
        }

        setTokens(data || []);

        // Cache in localStorage (only when we have real data)
        if (data && data.length > 0) {
          StorageService.set(`tokens_${portfolioId}`, data);
        }
      } else {
        // LOCAL MODE: Load tokens from PortfolioManager (tokens are INSIDE the portfolio)
        const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
        if (pmPortfolio && pmPortfolio.tokens && pmPortfolio.tokens.length > 0) {
          console.log('[LOCAL] Loading tokens from portfolio:', pmPortfolio.tokens.length);

          // Convert PortfolioManager tokens to LocalToken format
          const localTokens: LocalToken[] = pmPortfolio.tokens.map(t => ({
            id: t.id,
            portfolioId: portfolioId,
            symbol: t.displayName,
            name: t.displayName,
            targetPrice: t.targetPrice,
            notes: t.note,
            amount: 0,
            avgBuyPrice: 0,
          }));

          setTokens(localTokens);
        } else {
          // Fallback: try old 'tokens' key (shouldn't happen but safe)
          const localTokens = StorageService.get<LocalToken[]>('tokens', []);
          const portfolioTokens = localTokens.filter(t => t.portfolioId === portfolioId);
          setTokens(portfolioTokens);
        }
      }
    } catch (err) {
      console.error('Error fetching tokens:', err);
    }
  };

  /**
   * Create new portfolio
   */
  const createPortfolio = async (
    data: Partial<PortfolioInsert>
  ): Promise<Portfolio | LocalPortfolio> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        const { data: portfolio, error: insertError } = await supabase
          .from('portfolios')
          .insert({
            ...data,
            user_id: userId,
          } as PortfolioInsert)
          .select()
          .single();

        if (insertError) throw insertError;

        // Cache in localStorage
        const allPortfolios = [...portfolios, portfolio];
        StorageService.set('portfolios', allPortfolios);

        return portfolio;
      } else {
        // LOCAL MODE
        const localPortfolio: LocalPortfolio = {
          id: crypto.randomUUID(),
          name: data.name || 'New Portfolio',
          description: data.description || undefined,
          icon: data.icon || '💼',
          color: data.color || '#8b5cf6',
          isDefault: data.is_default || false,
          isArchived: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const allPortfolios = [...portfolios, localPortfolio];
        StorageService.set('portfolios', allPortfolios);
        setPortfolios(allPortfolios);

        return localPortfolio;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Update portfolio
   */
  const updatePortfolio = async (
    id: string,
    updates: Partial<PortfolioUpdate>
  ): Promise<Portfolio | LocalPortfolio> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        const { data, error: updateError } = await supabase
          .from('portfolios')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Update localStorage cache
        const allPortfolios = portfolios.map(p => p.id === id ? data : p);
        StorageService.set('portfolios', allPortfolios);

        return data;
      } else {
        // LOCAL MODE
        const allPortfolios = portfolios.map(p => {
          if (p.id === id) {
            return {
              ...p,
              ...updates,
              updatedAt: new Date().toISOString(),
            } as LocalPortfolio;
          }
          return p;
        });

        StorageService.set('portfolios', allPortfolios);
        setPortfolios(allPortfolios);

        return allPortfolios.find(p => p.id === id)!;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Delete portfolio (archive)
   */
  const deletePortfolio = async (id: string): Promise<void> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: Archive
        const { error: updateError } = await supabase
          .from('portfolios')
          .update({ is_archived: true })
          .eq('id', id);

        if (updateError) throw updateError;

        // Update localStorage cache
        const allPortfolios = portfolios.filter(p => p.id !== id);
        StorageService.set('portfolios', allPortfolios);
      } else {
        // LOCAL MODE: Remove
        const allPortfolios = portfolios.filter(p => p.id !== id);
        StorageService.set('portfolios', allPortfolios);
        setPortfolios(allPortfolios);
      }

      // If deleted portfolio was active, switch to another
      if (activePortfolio && activePortfolio.id === id) {
        const remaining = portfolios.filter((p) => p.id !== id);
        if (remaining.length > 0) {
          setActivePortfolioState(remaining[0]);
        } else {
          await createDefaultPortfolio();
        }
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Set active portfolio
   */
  const setActivePortfolio = (id: string): void => {
    const portfolio = portfolios.find((p) => p.id === id);
    if (portfolio) {
      setActivePortfolioState(portfolio);
    }
  };

  /**
   * Add token to active portfolio
   */
  const addToken = async (
    data: Partial<TokenInsert> & { id?: string; symbol: string }
  ): Promise<PortfolioToken | LocalToken> => {
    try {
      setError(null);

      if (!activePortfolio) {
        throw new Error('No active portfolio');
      }

      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        console.log('[CLOUD] Adding token:', {
          userId,
          portfolioId: activePortfolio.id,
          coinId: data.coin_id || data.id || data.symbol.toLowerCase(),
          symbol: data.symbol,
        });

        const { data: token, error: insertError } = await supabase
          .from('portfolio_tokens')
          .insert({
            user_id: userId,
            portfolio_id: activePortfolio.id,
            coin_id: data.coin_id || data.id || data.symbol.toLowerCase(),
            symbol: data.symbol,
            display_name: data.display_name || data.symbol,
            target_price: data.target_price?.toString(),
            notes: data.notes,
          } as TokenInsert)
          .select()
          .single();

        if (insertError) {
          console.error('[CLOUD] Error adding token:', insertError);
          throw insertError;
        }

        console.log('[CLOUD] Token added:', token);

        // CRITICAL: Update React state immediately (don't rely only on realtime)
        setTokens((prevTokens) => [...prevTokens, token]);

        // Also cache in localStorage
        const allTokens = [...tokens, token];
        StorageService.set(`tokens_${activePortfolio.id}`, allTokens);

        // V2.M.2 — emit add_token event.
        void trackTokenAdded({
          id: token.coin_id || token.id,
          symbol: token.symbol,
        });
        // V2.N.4 — mirror portfolio holdings to DB (debounced).
        scheduleDeviceSync();

        return token;
      } else {
        // LOCAL MODE: Save to PortfolioManager (NOT separate 'tokens' key!)
        const pmPortfolio = PortfolioManager.getPortfolio(activePortfolio.id);
        if (!pmPortfolio) {
          throw new Error('Portfolio not found in PortfolioManager');
        }

        // Create new token in PortfolioManager format
        const newToken: any = {
          id: data.id || data.symbol.toLowerCase(),
          displayName: data.display_name || data.symbol,
          positions: [], // Start with empty positions
          note: data.notes || undefined,
          tags: [],
          targetPrice: typeof data.target_price === 'string' ? parseFloat(data.target_price) : data.target_price,
        };

        // Add token to portfolio and save
        pmPortfolio.tokens.push(newToken);
        PortfolioManager.savePortfolio(pmPortfolio);

        // Update local state
        const localToken: LocalToken = {
          id: newToken.id,
          portfolioId: activePortfolio.id,
          symbol: newToken.displayName,
          name: newToken.displayName,
          targetPrice: newToken.targetPrice,
          notes: newToken.note,
          amount: 0,
          avgBuyPrice: 0,
        };

        setTokens([...tokens, localToken]);

        // V2.M.2 — emit add_token event.
        void trackTokenAdded({
          id: localToken.id,
          symbol: localToken.symbol,
        });
        // V2.N.4 — mirror portfolio holdings to DB (debounced).
        scheduleDeviceSync();

        return localToken;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Update token
   */
  const updateToken = async (
    id: string,
    updates: any
  ): Promise<PortfolioToken | LocalToken> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE
        const { data, error: updateError } = await supabase
          .from('portfolio_tokens')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Update localStorage cache
        if (activePortfolio) {
          const allTokens = tokens.map(t => t.id === id ? data : t);
          StorageService.set(`tokens_${activePortfolio.id}`, allTokens);
        }

        return data;
      } else {
        // LOCAL MODE: Update in PortfolioManager
        if (!activePortfolio) {
          throw new Error('No active portfolio');
        }

        const pmPortfolio = PortfolioManager.getPortfolio(activePortfolio.id);
        if (!pmPortfolio) {
          throw new Error('Portfolio not found in PortfolioManager');
        }

        // Find and update token in portfolio
        const tokenIndex = pmPortfolio.tokens.findIndex(t => t.id === id);
        if (tokenIndex === -1) {
          throw new Error('Token not found');
        }

        // Update token fields
        if (updates.targetPrice !== undefined) {
          pmPortfolio.tokens[tokenIndex].targetPrice = updates.targetPrice;
        }
        if (updates.notes !== undefined) {
          pmPortfolio.tokens[tokenIndex].note = updates.notes;
        }

        // Save portfolio
        PortfolioManager.savePortfolio(pmPortfolio);

        // Update local state
        const updatedTokens = tokens.map(t => {
          if (t.id === id) {
            return { ...t, ...updates };
          }
          return t;
        });
        setTokens(updatedTokens);

        return updatedTokens.find(t => t.id === id)!;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Delete token (archive in cloud, remove in local)
   */
  const deleteToken = async (id: string): Promise<void> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: Archive
        const { error: updateError } = await supabase
          .from('portfolio_tokens')
          .update({ is_archived: true })
          .eq('id', id);

        if (updateError) throw updateError;

        // Update localStorage cache
        if (activePortfolio) {
          const allTokens = tokens.filter(t => t.id !== id);
          StorageService.set(`tokens_${activePortfolio.id}`, allTokens);
        }
      } else {
        // LOCAL MODE: Remove from PortfolioManager
        if (!activePortfolio) {
          throw new Error('No active portfolio');
        }

        const pmPortfolio = PortfolioManager.getPortfolio(activePortfolio.id);
        if (!pmPortfolio) {
          throw new Error('Portfolio not found in PortfolioManager');
        }

        // Remove token from portfolio
        pmPortfolio.tokens = pmPortfolio.tokens.filter(t => t.id !== id);
        PortfolioManager.savePortfolio(pmPortfolio);

        // Update local state
        setTokens(tokens.filter(t => t.id !== id));
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return {
    // State
    portfolios,
    activePortfolio,
    loading,
    error,
    mode,

    // Portfolio actions
    createPortfolio,
    updatePortfolio,
    deletePortfolio,
    setActivePortfolio,

    // Tokens
    tokens,
    addToken,
    updateToken,
    deleteToken,
  };
}
